import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { getFreshAccessToken } from '../../utils/authUtils';
import { api } from '@expat-ui/shared';
import { Country, Currency } from '../../types/api.types';
import styles from './OnboardingPage.module.scss';

type Step = 'household' | 'profile';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

const CURRENCIES: { value: Currency; label: string }[] = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'INR', label: 'INR (₹)' },
    { value: 'CAD', label: 'CAD (C$)' },
];

export function OnboardingPage() {
    const navigate = useNavigate();
    const { user, markHouseholdCreated } = useAuth();
    const [step, setStep] = useState<Step>('household');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [birthYear, setBirthYear] = useState(String(new Date().getFullYear() - 35));
    const [targetRetirementAge, setTargetRetirementAge] = useState('55');
    const [baseCurrency, setBaseCurrency] = useState<Currency | ''>('');
    const [currentCountry, setCurrentCountry] = useState<Country | ''>('');
    const [householdName, setHouseholdName] = useState(`${user?.firstName || 'My'}'s Household`);

    const handleHouseholdSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            await api.post('/household', { name: householdName }, token);
            markHouseholdCreated();
            setStep('profile');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create household');
        } finally {
            setSaving(false);
        }
    };

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');

            await api.put(
                '/profile',
                {
                    birthYear: Number(birthYear),
                    targetRetirementAge: Number(targetRetirementAge),
                    baseCurrency: baseCurrency as Currency,
                    displayName: user?.firstName || user?.email || 'User',
                    residencyHistory: [
                        {
                            startAge: new Date().getFullYear() - Number(birthYear),
                            endAge: Number(targetRetirementAge),
                            country: currentCountry as Country,
                        },
                    ],
                },
                token,
            );
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.progress}>
                    <div className={`${styles.dot} ${step === 'household' ? styles.active : styles.completed}`} />
                    <div className={styles.line} />
                    <div className={`${styles.dot} ${step === 'profile' ? styles.active : ''}`} />
                </div>

                {step === 'household' && (
                    <form onSubmit={handleHouseholdSubmit}>
                        <h2 className={styles.heading}>Create Your Household</h2>
                        <p className={styles.subheading}>
                            A household groups your family&apos;s cross-border finances together.
                        </p>

                        <div className={styles.field}>
                            <label className={styles.label}>Household Name</label>
                            <input
                                type="text"
                                className={styles.input}
                                value={householdName}
                                onChange={(e) => setHouseholdName(e.target.value)}
                                required
                            />
                        </div>

                        {error && <p className={styles.error}>{error}</p>}

                        <button type="submit" className={styles.button} disabled={saving}>
                            {saving ? 'Creating...' : 'Continue'}
                        </button>
                    </form>
                )}

                {step === 'profile' && (
                    <form onSubmit={handleProfileSubmit}>
                        <h2 className={styles.heading}>Set Up Your Profile</h2>
                        <p className={styles.subheading}>Tell us about your financial planning horizon.</p>

                        <div className={styles.field}>
                            <label className={styles.label}>Year of Birth</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={birthYear}
                                onChange={(e) => setBirthYear(e.target.value)}
                                min={1940}
                                max={new Date().getFullYear() - 18}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Target Retirement Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={targetRetirementAge}
                                onChange={(e) => setTargetRetirementAge(e.target.value)}
                                min={new Date().getFullYear() - Number(birthYear) + 1}
                                max={90}
                                required
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Current Country of Residence</label>
                            <select
                                className={styles.input}
                                value={currentCountry}
                                onChange={(e) => setCurrentCountry(e.target.value as Country)}
                                required
                            >
                                <option value="" disabled>
                                    Select country
                                </option>
                                {COUNTRIES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Base Display Currency</label>
                            <select
                                className={styles.input}
                                value={baseCurrency}
                                onChange={(e) => setBaseCurrency(e.target.value as Currency)}
                                required
                            >
                                <option value="" disabled>
                                    Select currency
                                </option>
                                {CURRENCIES.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {error && <p className={styles.error}>{error}</p>}

                        <button type="submit" className={styles.button} disabled={saving}>
                            {saving ? 'Saving...' : 'Finish Setup'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
